import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { db } from '../../firebase/firebase-config';
import { BANCOS_MEXICO } from '../../helpers/util';
import Swal from 'sweetalert2';

const SPEI_VACIO = { a_nombre_de: 'X', banco: 'X', clabe_o_tarjeta: '000000000000000000' };

const tieneAnticiposConfigurados = (spei) => {
    if (!spei) return false;
    return spei.a_nombre_de !== 'X' && spei.clabe_o_tarjeta !== '000000000000000000';
};

export const PagosScreen = ({ onBack }) => {
    const { hashtag } = useSelector(state => state.auth);
    const [spei, setSpei] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [editando, setEditando] = useState(false);
    const [nombre, setNombre] = useState('');
    const [banco, setBanco] = useState('');
    const [clabe, setClabe] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [manejaAnticipo, setManejaAnticipo] = useState(false);

    useEffect(() => {
        const cargar = async () => {
            try {
                const snap = await db.doc(`general/${hashtag}`).get();
                const data = snap.data();
                const speiData = data?.spei || SPEI_VACIO;
                setSpei(speiData);
                const configurado = tieneAnticiposConfigurados(speiData);
                setManejaAnticipo(configurado);
                if (configurado) {
                    setNombre(speiData.a_nombre_de);
                    setBanco(speiData.banco);
                    setClabe(speiData.clabe_o_tarjeta);
                }
            } catch (e) {
                console.log('Error:', e);
            } finally {
                setCargando(false);
            }
        };
        cargar();
    }, [hashtag]);

    const handleGuardar = async () => {
        if (!nombre.trim()) { Swal.fire('Falta el nombre', 'Escribe el nombre del titular de la cuenta.', 'warning'); return; }
        if (!banco) { Swal.fire('Falta el banco', 'Selecciona tu banco.', 'warning'); return; }
        if (clabe.length !== 18) { Swal.fire('CLABE inválida', 'La CLABE debe tener exactamente 18 dígitos.', 'warning'); return; }

        setGuardando(true);
        try {
            const nuevoSpei = { a_nombre_de: nombre.trim(), banco, clabe_o_tarjeta: clabe };
            await db.doc(`general/${hashtag}`).update({ spei: nuevoSpei });
            setSpei(nuevoSpei);
            setManejaAnticipo(true);
            setEditando(false);
            Swal.fire({ icon: 'success', title: '¡Datos guardados!', text: 'Ya puedes configurar anticipos en tus servicios.', confirmButtonColor: '#D4537E' });
        } catch (e) {
            Swal.fire('Error', 'No se pudo guardar. Intenta de nuevo.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    const handleDesactivar = async () => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Desactivar anticipos?',
            text: 'Tus servicios ya no pedirán anticipo y los montos configurados se pondrán en cero.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#D4537E',
            confirmButtonText: 'Sí, desactivar',
            cancelButtonText: 'Cancelar',
        });
        if (!isConfirmed) return;

        setGuardando(true);
        try {
            // Actualizar SPEI a valores vacíos
            await db.doc(`general/${hashtag}`).update({ spei: SPEI_VACIO });

            // Limpiar montoAnticipo en catálogo
            const catSnap = await db.doc(`general/${hashtag}/servicios/catalogo`).get();
            if (catSnap.exists) {
                const servicios = (catSnap.data().servicios || []).map(s => ({ ...s, montoAnticipo: 0, porcentajeAnticipo: 0 }));
                await db.doc(`general/${hashtag}/servicios/catalogo`).set({ servicios });
            }

            // Limpiar montoAnticipo en todos los consultores
            const colSnap = await db.collection(`general/${hashtag}/consultores`).get();
            for (const colDoc of colSnap.docs) {
                const listaSnap = await db.doc(`general/${hashtag}/consultores/${colDoc.id}/servicios/lista`).get();
                if (listaSnap.exists) {
                    const servicios = (listaSnap.data().servicios || []).map(s => ({ ...s, montoAnticipo: 0, porcentajeAnticipo: 0 }));
                    await db.doc(`general/${hashtag}/consultores/${colDoc.id}/servicios/lista`).set({ servicios });
                }
            }

            setSpei(SPEI_VACIO);
            setManejaAnticipo(false);
            setNombre('');
            setBanco('');
            setClabe('');
            setEditando(false);
            Swal.fire({ icon: 'success', title: 'Anticipos desactivados', timer: 1500, showConfirmButton: false })
                .then(() => onBack());
        } catch (e) {
            Swal.fire('Error', 'No se pudo desactivar. Intenta de nuevo.', 'error');
        } finally {
            setGuardando(false);
        }
    };

    if (cargando) return (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 32 }}>💳</div>
            <p style={{ color: 'var(--pink-800)' }}>Cargando...</p>
        </div>
    );

    return (
        <div className="app-container">
            <div className="app-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--pink-600)', fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>←</button>
                <div>
                    <h1>Pagos y anticipos</h1>
                    <p>{manejaAnticipo ? 'Anticipos activos' : 'Sin anticipos'}</p>
                </div>
            </div>

            <div className="screen-body">
                {!manejaAnticipo && !editando && (
                    <>
                        <div className="info-box" style={{ marginTop: 0, background: '#FFF3E0', border: '0.5px solid #FFB74D' }}>
                            <div className="info-box-title" style={{ color: '#E65100' }}>⚠️ Antes de activar los anticipos</div>
                            <div className="info-box-body" style={{ color: '#BF360C' }}>
                                Te recomendamos activar esta opción <strong>solo si ya venías cobrando anticipo con tus clientas</strong>. Si es algo nuevo para ellas, podría hacer que duden antes de reservar.<br /><br />
                                Recuerda que si tu clienta no envía el anticipo a tiempo, el sistema cancela su espacio automáticamente.
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
                            <button className="btn-outline" onClick={onBack}>No por ahora</button>
                            <button className="btn-primary" style={{ marginTop: 0 }} onClick={() => setEditando(true)}>Activar anticipos</button>
                        </div>
                    </>
                )}

                {manejaAnticipo && !editando && (
                    <>
                        <div className="info-box" style={{ marginTop: 0 }}>
                            <div className="info-box-title">✓ Anticipos configurados</div>
                            <div className="info-box-body">
                                <strong>Titular:</strong> {spei?.a_nombre_de}<br />
                                <strong>Banco:</strong> {spei?.banco}<br />
                                <strong>CLABE:</strong> ••••••••••••{spei?.clabe_o_tarjeta?.slice(-4)}
                            </div>
                        </div>

                        <div className="info-box" style={{ marginTop: 10 }}>
                            <div className="info-box-body">
                                Para configurar el monto de anticipo por servicio, ve a <strong>Servicios y precios</strong> y edita cada servicio.
                            </div>
                        </div>

                        <button className="btn-primary" style={{ marginTop: 16 }} onClick={() => setEditando(true)}>
                            Editar datos bancarios
                        </button>
                        <button className="btn-secondary" onClick={handleDesactivar} disabled={guardando}>
                            Desactivar anticipos
                        </button>
                    </>
                )}

                {editando && (
                    <>
                        <div className="info-box" style={{ marginTop: 0 }}>
                            <div className="info-box-body">
                                Tus clientas te enviarán el anticipo por transferencia SPEI a estos datos.
                            </div>
                        </div>

                        <div className="field-label" style={{ marginTop: 16 }}>Nombre del titular de la cuenta</div>
                        <input className="field-input" placeholder="Como aparece en tu banco"
                            value={nombre} onChange={e => setNombre(e.target.value)} />

                        <div className="field-label">Banco</div>
                        <select className="field-input" value={banco} onChange={e => setBanco(e.target.value)}>
                            <option value="">Selecciona tu banco</option>
                            {BANCOS_MEXICO.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>

                        <div className="field-label">CLABE interbancaria</div>
                        <input className="field-input" placeholder="18 dígitos" type="tel" inputMode="numeric"
                            value={clabe} onChange={e => setClabe(e.target.value.replace(/\D/g, '').slice(0, 18))} maxLength={18} />
                        <p className={`field-${clabe.length === 18 ? 'hint' : clabe.length > 0 ? 'error' : 'hint'}`}>
                            {clabe.length === 18 ? '✓ CLABE válida' : clabe.length > 0 ? `${clabe.length}/18 dígitos` : 'Solo números, sin espacios ni guiones'}
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
                            <button className="btn-outline" onClick={() => setEditando(false)}>Cancelar</button>
                            <button className="btn-primary" style={{ marginTop: 0 }} onClick={handleGuardar} disabled={guardando}>
                                {guardando ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
